<script setup>
import { router } from '@inertiajs/vue3';
import { getCurrentInstance, computed, ref, reactive, watch, nextTick } from 'vue';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import { useLoading } from '@/composables/useLoading';
import { useForm } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';

import ClientInformationSection from '@/components/invoice/ClientInformationSection.vue';
import CompanySelectionSection from '@/components/invoice/CompanySelectionSection.vue';
import BankAccountSelectionSection from '@/components/invoice/BankAccountSelectionSection.vue';
import PaymentTaxDiscountVatSection from '@/components/invoice/PaymentTaxDiscountVatSection.vue';
import InvoiceSettingsSection from '@/components/invoice/InvoiceSettingsSection.vue';
import PaymentInformationSection from '@/components/payment/PaymentInformationSection.vue';
import CompanyFormModal from '@/components/admin/CompanyFormModal.vue';
import BankAccountFormModal from '@/components/admin/BankAccountFormModal.vue';
import PaymentRequestMismatchModal from '@/components/modals/PaymentRequestMismatchModal.vue';
import RecipientContactBlock from '@/components/admin/orders/RecipientContactBlock.vue';
import ClientEmailMessagePanel from '@/components/admin/ClientEmailMessagePanel.vue';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();
const toast = useToast();
const confirm = useConfirm();

const props = defineProps({
    order: {
        type: Object,
        required: true
    },
    invoice: {
        type: [Object, null],
        required: false,
        default: () => null
    },
    isCreateMode: {
        type: Boolean,
        default: false
    },
    userType: {
        type: String,
        default: 'client',
        validator: (value) => ['admin', 'client'].includes(value)
    },
    companies: {
        type: Array,
        default: () => []
    },
    bankAccounts: {
        type: Array,
        default: () => []
    },
    invoiceSettings: {
        type: Object,
        default: () => ({})
    },
    paymentRequest: {
        type: Object,
        default: () => null
    },
    publicationRecipients: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['close']);

const confirmPaymentForm = useForm({});
const showConfirmDialog = ref(false);

const isEditMode = ref(props.isCreateMode);
const showPreviewModal = ref(false);
const showAddFieldDialog = ref(false);
const sendToClient = ref(true);

const paymentLinkOption = ref('bank_account');
const customPaymentLink = ref('');

const showEditConfirmDialog = ref(false);
const showSendEmailDialog = ref(false);
const showValueDifferenceDialog = ref(false);
const valueDifferenceData = ref(null);
const sendEmailForm = useForm({});
const invoiceEmailOptions = ref({ message: null, locale: null });

const showCompanyModal = ref(false);
const showBankAccountModal = ref(false);
const editingCompany = ref(null);
const editingBankAccount = ref(null);

const selectedCompany = ref(null);
const selectedBankAccount = ref(null);

const selectedInvoiceTaxes = ref([]);
const showInvoiceSettingsModal = ref(false);
const showCustomTaxModal = ref(false);
const availableSettings = computed(() => {
    return Object.values(props.invoiceSettings).flat();
});

const customTaxForm = useForm({
    key: '',
    description: '',
    value: 0,
    type: 'percentage',
    currency: 'USD',
    group: 'fees'
});

const initializeInvoiceTaxes = () => {
    if (props.invoice?.invoice_taxes && props.invoice.invoice_taxes.length > 0) {
        selectedInvoiceTaxes.value = props.invoice.invoice_taxes.map(tax => ({
            invoice_setting_id: tax.invoice_setting_id,
            value: tax.value,
            setting: tax.invoice_setting
        }));
    }
};

const invoiceData = reactive({
    number: props.invoice?.number || null,
    created_at: props.invoice?.created_at || (props.isCreateMode ? new Date().toISOString().split('T')[0] : null),
    bank: props.invoice?.bank || 'WISE',

    vatRate: props.invoice?.vat_rate || 0,

    paymentTax: 0,
    paymentTaxCoin: props.invoice?.payment_tax_coin || props.invoice?.value_coin || props.order?.order_items?.[0]?.sale_coin || 'USD',

    discount: (props.invoice?.discount || 0) / 100,
    discountCoin: props.invoice?.discount_coin || props.order?.order_items?.[0]?.sale_coin || 'USD',

    selected_company_id: null,
    selected_bank_account_id: null,

    company_name: 'LINKARME - LINK BUILDING LLC',
    company_street: '6751 FORUM DRIVE SUITE 240',
    company_city: 'Orlando',
    company_state: 'Florida, United States',
    company_ein: '88-3088727',
    company_vat: '—//—',
    company_email: 'financeiro@linkarme.com'
});

const initializeSelectedValues = () => {
    const paymentRequestData = props.paymentRequest || props.order?.payment_request;

    if (props.invoice?.company_profile_id && props.companies.length > 0 && !selectedCompany.value) {
        selectedCompany.value = props.companies.find(company => company.id == props.invoice.company_profile_id) || null;
    }

    if (props.invoice?.bank_account_id && props.bankAccounts.length > 0 && !selectedBankAccount.value) {
        selectedBankAccount.value = props.bankAccounts.find(account => account.id == props.invoice.bank_account_id) || null;
    }

    if (props.isCreateMode && paymentRequestData) {
        if (paymentRequestData.company_profile_id && props.companies.length > 0 && !selectedCompany.value) {
            selectedCompany.value = props.companies.find(company => company.id == paymentRequestData.company_profile_id) || null;
        }

        if (paymentRequestData.bank_account_id && props.bankAccounts.length > 0 && !selectedBankAccount.value) {
            selectedBankAccount.value = props.bankAccounts.find(account => account.id == paymentRequestData.bank_account_id) || null;
        }
    }

    invoiceData.paymentTax = props.isCreateMode ?
        (calculateOriginalOrderPaymentTax() / 100) :
        (props.invoice?.payment_tax ? (props.invoice.payment_tax / 100) : (calculateOriginalOrderPaymentTax() / 100));

    const paymentLinkSource = props.invoice?.payment_link || (props.isCreateMode && paymentRequestData?.payment_link);

    if (paymentLinkSource) {
        if (!props.isCreateMode) {
            if (selectedBankAccount.value?.payment_link === paymentLinkSource) {
                paymentLinkOption.value = 'bank_account';
                customPaymentLink.value = '';
            } else {
                paymentLinkOption.value = 'custom';
                customPaymentLink.value = paymentLinkSource;
            }
        } else {
            if (paymentRequestData?.payment_link && selectedBankAccount.value?.payment_link === paymentRequestData.payment_link) {
                paymentLinkOption.value = 'bank_account';
                customPaymentLink.value = '';
            } else {
                paymentLinkOption.value = 'custom';
                customPaymentLink.value = paymentLinkSource;
            }
        }
    } else {
        if (selectedBankAccount.value?.payment_link) {
            paymentLinkOption.value = 'bank_account';
            customPaymentLink.value = '';
        } else {
            paymentLinkOption.value = 'none';
            customPaymentLink.value = '';
        }
    }
};

const calculateOriginalOrderPaymentTax = () => {
    if (!props.order || !props.order.order_items) return 0;
    return props.order.order_items.reduce((sum, item) => sum + (item.payment_tax || 0), 0);
};

initializeSelectedValues();
initializeInvoiceTaxes();

watch([() => props.companies, () => props.bankAccounts, () => props.invoice, () => props.paymentRequest], () => {
    initializeSelectedValues();
    initializeInvoiceTaxes();
}, { deep: true });

const showDivergenceDialog = ref(false);
const pendingSaveCallback = ref(null);

const isInvoiceGenerated = computed(() => {
    if (props.isCreateMode) return false;
    return props.invoice && props.invoice.created_at && (
        props.invoice.bank ||
        props.invoice.payment_link ||
        props.invoice.vat_rate > 0 ||
        props.invoice.discount > 0 ||
        (props.invoice.invoice_taxes && props.invoice.invoice_taxes.length > 0)
    );
});

const fieldTypes = computed(() => {
    return availableSettings.value.map(setting => ({
        label: `${setting.key} (${setting.description})`,
        value: setting.key,
        defaultRate: setting.value,
        type: setting.type,
        currency: setting.currency
    }));
});

const selectedFieldType = ref(null);
const isGeneratingInvoice = ref(false);

const canGenerateInvoice = computed(() => {
    return selectedCompany.value && selectedBankAccount.value;
});

const saveForm = useForm({
    invoice_data: {},
    dynamic_fields: []
});

const { formatCents } = useCurrencyFormatter();

const formatCurrency = (value, currency) => {
    if (!value || !currency) return '0.00';
    return formatCents(value, currency);
};

const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

const downloadInvoice = () => {
    if (props.invoice?.id) {
        const url = props.userType === 'client'
            ? route('client.orders.invoice.download', props.order.id)
            : route('invoices.generate', props.invoice.id);
        window.open(url, '_blank');
    }
};

const goBack = () => {
    const routeName = props.userType === 'admin' ? 'orders.index' : 'client.orders.index';
    router.visit(route(routeName), {
        onStart: () => showLoading($t('Loading...')),
        onFinish: () => hideLoading()
    });
};

const viewOrder = () => {
    if (props.userType === 'admin') {
        router.visit(route('orders.edit', props.order.id), {
            onStart: () => showLoading($t('Loading...')),
            onFinish: () => hideLoading()
        });
    }
};

const getCurrencySymbol = (currency) => {
    const symbols = {
        'EUR': '€',
        'USD': '$',
        'BRL': 'R$',
        'GBP': '£'
    };
    return symbols[currency] || currency;
};

const invoiceItems = computed(() => {
    if (!props.order || !props.order.order_items) return [];

    return props.order.order_items.map((item, index) => {
        const recipient = props.publicationRecipients?.[item.id] || null;
        return {
            id: item.id || index,
            domain: item.site?.url || 'N/A',
            price: formatCurrency(item.sale, item.sale_coin),
            payment_tax: item.payment_tax || 0,
            currency: item.sale_coin,
            paymentTaxCurrency: item.sale_coin,
            currency_symbol: getCurrencySymbol(item.sale_coin),
            live_link: item.link || 'N/A',
            confirmed_at: item.live_link_confirmed_at ? formatDate(item.live_link_confirmed_at) : 'Not confirmed',
            editablePrice: (item.sale || 0) / 100,
            editablePaymentTax: (item.payment_tax || 0) / 100,
            originalSale: item.sale || 0,
            originalPaymentTax: item.payment_tax || 0,
            publication_recipient: recipient,
        };
    });
});

const wrapperClass = computed(() => {
    return props.userType === 'client'
        ? 'grid px-2 sm:px-4 lg:px-0 w-full max-w-full'
        : 'grid';
});

const buttonSize = computed(() => {
    return props.userType === 'client' ? 'small' : undefined;
});

const isPaymentDeferred = computed(() => {
    return props.order.deferred_payment && !props.order.payment_date;
});

const canShowPaymentActions = computed(() => {
    return isPaymentDeferred.value;
});

const canDownloadPDF = computed(() => {
    return !props.isCreateMode && props.invoice?.id;
});

const invoiceCurrency = computed(() => {
    return props.invoice?.value_coin || props.order?.order_items?.[0]?.sale_coin || 'USD';
});

const finalPaymentLink = computed(() => {
    switch (paymentLinkOption.value) {
        case 'bank_account':
            return selectedBankAccount.value?.payment_link || '';
        case 'custom':
            return customPaymentLink.value || '';
        case 'none':
        default:
            return '';
    }
});

const companyModalTitle = computed(() => {
    if (selectedCompany.value && selectedCompany.value.id && !editingCompany.value) {
        return $t('Company Details');
    }
    return selectedCompany.value && selectedCompany.value.id ? $t('Edit Company') : $t('New Company');
});

const bankAccountModalTitle = computed(() => {
    if (selectedBankAccount.value && selectedBankAccount.value.id && !editingBankAccount.value) {
        return $t('Bank Account Details');
    }
    return selectedBankAccount.value && selectedBankAccount.value.id ? $t('Edit Bank Account') : $t('New Bank Account');
});


const calculateSubtotal = () => {
    if (!props.order || !props.order.order_items) return 0;
    return props.order.order_items.reduce((sum, item) => sum + (item.sale || 0), 0);
};

const calculatePaymentTax = () => {
    return props.invoice?.payment_tax || calculateOriginalOrderPaymentTax();
};

const calculateTotal = () => {
    return calculateSubtotal() + calculatePaymentTax();
};

const toggleEditMode = () => {
    if (props.isCreateMode) {
        isEditMode.value = !isEditMode.value;
        if (!isEditMode.value) {
            resetInvoiceData();
        }
        return;
    }

    if (!isEditMode.value && isInvoiceGenerated.value && props.order.payment_date) {
        showEditConfirmDialog.value = true;
    } else {
        isEditMode.value = !isEditMode.value;
        if (!isEditMode.value) {
            resetInvoiceData();
        }
    }
};

const confirmEditExistingInvoice = () => {
    showEditConfirmDialog.value = false;
    isEditMode.value = true;
};

const cancelEditConfirmation = () => {
    showEditConfirmDialog.value = false;
};

const resetInvoiceData = () => {
    invoiceData.number = props.invoice?.number || null;
    invoiceData.created_at = props.invoice?.created_at || (props.isCreateMode ? new Date().toISOString().split('T')[0] : null);
    invoiceData.bank = props.invoice?.bank || 'WISE';
    invoiceData.vatRate = props.invoice?.vat_rate || 0;
    invoiceData.discount = (props.invoice?.discount || 0) / 100;
    invoiceData.discountCoin = props.invoice?.discount_coin || props.order?.order_items?.[0]?.sale_coin || 'USD';

    initializeSelectedValues();
};

const hasValueChanges = () => {
    const originalSubtotal = calculateSubtotal();
    const editedSubtotal = calculateEditedSubtotal();

    const originalPaymentTax = calculateOriginalOrderPaymentTax();
    const editedPaymentTax = (invoiceData.paymentTax || 0) * 100;

    const originalVat = props.invoice?.vat_rate || 0;
    const editedVat = invoiceData.vatRate;

    const originalDiscount = props.invoice?.discount || 0;
    const editedDiscount = Math.round(invoiceData.discount * 100);

    const originalTaxes = props.invoice?.invoice_taxes || [];
    const hasChangedTaxes = originalTaxes.length !== selectedInvoiceTaxes.value.length ||
        !originalTaxes.every(originalTax =>
            selectedInvoiceTaxes.value.some(newTax =>
                newTax.invoice_setting_id === originalTax.invoice_setting_id &&
                newTax.value === originalTax.value
            )
        );

    return originalSubtotal !== editedSubtotal ||
           originalPaymentTax !== editedPaymentTax ||
           originalVat !== editedVat ||
           originalDiscount !== editedDiscount ||
           hasChangedTaxes;
};

watch(selectedCompany, (newCompany) => {
    if (newCompany && newCompany.id !== props.invoice?.company_profile_id) {
        onCompanyChange();
        if (!props.isCreateMode && props.invoice?.id) {
            router.put(route('invoices.update', props.invoice.id), {
                company_profile_id: newCompany.id
            }, {
                preserveScroll: true,
                onSuccess: () => {
                    toast.add({
                        severity: 'success',
                        summary: $t('Success'),
                        detail: $t('Company updated successfully'),
                        life: 3000
                    });
                }
            });
        }
    }
});

watch(selectedBankAccount, (newBankAccount) => {
    if (newBankAccount && newBankAccount.id !== props.invoice?.bank_account_id) {
        onBankAccountChange();
        if (!props.isCreateMode && props.invoice?.id) {
            router.put(route('invoices.update', props.invoice.id), {
                bank_account_id: newBankAccount.id
            }, {
                preserveScroll: true,
                onSuccess: () => {
                    toast.add({
                        severity: 'success',
                        summary: $t('Success'),
                        detail: $t('Bank account updated successfully'),
                        life: 3000
                    });
                }
            });
        }
    }
});

watch(() => props.invoice?.value_coin || props.order?.order_items?.[0]?.sale_coin, (newCurrency) => {
    if (newCurrency) {
        invoiceData.discountCoin = newCurrency;
        if (!props.invoice?.payment_tax_coin) {
            invoiceData.paymentTaxCoin = newCurrency;
        }
    }
}, { immediate: true });

const showPreview = () => {
    showPreviewModal.value = true;
};

const saveInvoiceChanges = () => {
    // In create mode, warn that generating the invoice locks the order (no more item add/cancel)
    if (props.isCreateMode) {
        confirm.require({
            group: 'invoice-generate',
            message: $t('After generating the invoice, the order will be locked and items can no longer be added or canceled. Continue?'),
            header: $t('Generate Invoice'),
            icon: 'pi pi-exclamation-triangle',
            rejectClass: 'p-button-secondary p-button-outlined',
            acceptClass: 'p-button-success',
            accept: () => proceedSaveInvoiceChanges(),
        });
        return;
    }
    proceedSaveInvoiceChanges();
};

const proceedSaveInvoiceChanges = () => {
    if (!selectedCompany.value) {
        toast.add({
            severity: 'error',
            summary: $t('Validation Error'),
            detail: $t('Please select a company'),
            life: 5000
        });
        return;
    }

    if (!selectedBankAccount.value) {
        toast.add({
            severity: 'error',
            summary: $t('Validation Error'),
            detail: $t('Please select a bank account'),
            life: 5000
        });
        return;
    }

    const paymentRequestData = props.paymentRequest || props.order?.payment_request;
    if (!props.isCreateMode && paymentRequestData && props.userType === 'admin') {
        const companyDiverges = selectedCompany.value?.id !== paymentRequestData.company_profile_id;
        const bankAccountDiverges = selectedBankAccount.value?.id !== paymentRequestData.bank_account_id;
        const paymentLinkDiverges = finalPaymentLink.value !== paymentRequestData.payment_link;

        if (companyDiverges || bankAccountDiverges || paymentLinkDiverges) {
            pendingSaveCallback.value = performInvoiceSave;
            showDivergenceDialog.value = true;
            return;
        }
    }

    performInvoiceSave();
};

const proceedWithDivergence = () => {
    showDivergenceDialog.value = false;
    if (pendingSaveCallback.value) {
        pendingSaveCallback.value();
        pendingSaveCallback.value = null;
    }
};

const cancelDivergence = () => {
    showDivergenceDialog.value = false;
    pendingSaveCallback.value = null;
};

const performInvoiceSave = async () => {
    isGeneratingInvoice.value = true;
    showLoading(props.isCreateMode ? $t('Creating invoice...') : $t('Updating invoice...'));
    await nextTick();

    const totalValue = calculateTotalWithDynamicFields();

    const paymentTax = isEditMode.value ? calculateEditedPaymentTax() : calculatePaymentTax();

    const formData = {
        order_id: props.order.id,
        bank: invoiceData.bank,
        payment_link: finalPaymentLink.value,
        vat_rate: invoiceData.vatRate,
        discount: Math.round(invoiceData.discount * 100),
        discount_coin: invoiceData.discountCoin,
        value: Math.round(totalValue),
        value_coin: props.invoice?.value_coin || props.order?.order_items?.[0]?.sale_coin || 'USD',
        payment_tax: Math.round(paymentTax),
        payment_tax_coin: invoiceData.paymentTaxCoin,
        company_profile_id: selectedCompany.value?.id || null,
        bank_account_id: selectedBankAccount.value?.id || null,

        invoice_taxes: selectedInvoiceTaxes.value.map(tax => ({
            invoice_setting_id: tax.invoice_setting_id,
            value: tax.value
        }))
    };

    if (!props.isCreateMode && invoiceData.number) {
        formData.number = invoiceData.number;
    }

    if (props.isCreateMode) {
        fetch(route('invoices.store'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                'Accept': 'application/json',
            },
            body: JSON.stringify({
                selected: [props.order.id],
                send_to_client: sendToClient.value,
                invoice_data: formData
            })
        })
        .then(response => {
            if (response.redirected) {
                const url = response.url;
                const matches = url.match(/\/invoices\/(\d+)\/edit/);
                if (matches && matches[1]) {
                    const invoiceId = matches[1];

                    showLoading($t('Updating invoice data...'));

                    router.put(route('invoices.update', invoiceId), formData, {
                        onSuccess: () => {
                            isGeneratingInvoice.value = false;
                            hideLoading();
                            isEditMode.value = false;
                            showPreviewModal.value = false;

                            toast.add({
                                severity: 'success',
                                summary: $t('Success'),
                                detail: sendToClient.value ?
                                    $t('Invoice created and sent to client successfully') :
                                    $t('Invoice created successfully'),
                                life: 3000
                            });

                            if (props.userType === 'admin') {
                                emit('close');
                            }
                        },
                        onError: (errors) => {
                            isGeneratingInvoice.value = false;
                            hideLoading();
                            console.error('Error updating invoice:', errors);
                            toast.add({
                                severity: 'error',
                                summary: $t('Error'),
                                detail: $t('Failed to update invoice'),
                                life: 5000
                            });
                        }
                    });
                } else {
                    isGeneratingInvoice.value = false;
                    hideLoading();
                    toast.add({
                        severity: 'success',
                        summary: $t('Success'),
                        detail: sendToClient.value ?
                            $t('Invoice created and sent to client successfully') :
                            $t('Invoice created successfully'),
                        life: 3000
                    });

                    if (props.userType === 'admin') {
                        emit('close');
                    }
                }
            } else {
                return response.json();
            }
        })
        .catch(error => {
            isGeneratingInvoice.value = false;
            hideLoading();
            console.error('Error creating invoice:', error);
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to create invoice'),
                life: 5000
            });
        });
        return;
    }

    router.put(route('invoices.update', props.invoice.id), formData, {
        onSuccess: (response) => {
            isGeneratingInvoice.value = false;
            hideLoading();
            isEditMode.value = false;
            showPreviewModal.value = false;

            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Invoice updated successfully'),
                life: 3000
            });

            if (isInvoiceGenerated.value) {
                showSendEmailDialog.value = true;
            } else {
                window.location.reload();
            }
        },
        onError: (errors) => {
            isGeneratingInvoice.value = false;
            hideLoading();
            console.error('Error updating invoice:', errors);
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to update invoice'),
                life: 5000
            });
        }
    });
};

const proceedWithSave = () => {
    showValueDifferenceDialog.value = false;
    performInvoiceSave();
};

const cancelSave = () => {
    showValueDifferenceDialog.value = false;
    isGeneratingInvoice.value = false;
    hideLoading();
};

const sendNewInvoiceVersion = () => {
    if (!props.invoice?.id) return;

    sendEmailForm
        .transform(data => ({
            ...data,
            client_message: invoiceEmailOptions.value.message,
            email_locale: invoiceEmailOptions.value.locale,
        }))
        .post(route('invoices.send-updated', props.invoice.id), {
        onSuccess: () => {
            showSendEmailDialog.value = false;
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('New invoice version sent successfully'),
                life: 3000
            });
            showSendEmailDialog.value = false;
        },
        onError: (errors) => {
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to send invoice'),
                life: 5000
            });
        }
    });
};

const skipSendingEmail = () => {
    showSendEmailDialog.value = false;
};

const onCompanyChange = () => {
    if (selectedCompany.value) {
        const company = props.companies.find(c => c.id === selectedCompany.value.id);
        if (company) {
            invoiceData.selected_company_id = company.id;
            invoiceData.company_name = company.name;
            invoiceData.company_street = company.address_street + (company.address_number ? ' ' + company.address_number : '');
            invoiceData.company_city = company.address_city;
            invoiceData.company_state = company.address_state + (company.address_country ? ', ' + company.address_country : '');
            invoiceData.company_ein = company.document_number;
            invoiceData.company_vat = company.vat_number || '—//—';
            invoiceData.company_email = company.email;
        }
    }
};

const openCompanyModal = () => {
    editingCompany.value = selectedCompany.value;
    showCompanyModal.value = true;
};

const createNewCompany = () => {
    editingCompany.value = null;
    showCompanyModal.value = true;
};

const handleCompanySaved = () => {
    showCompanyModal.value = false;
    router.reload({ only: ['companies', 'bankAccounts'], preserveScroll: true, preserveState: true });
};

const onBankAccountChange = () => {
    if (selectedBankAccount.value) {
        const bankAccount = props.bankAccounts.find(ba => ba.id === selectedBankAccount.value.id);
        if (bankAccount) {
            invoiceData.selected_bank_account_id = bankAccount.id;
            invoiceData.bank = bankAccount.name;

            if (bankAccount.payment_link && (paymentLinkOption.value === 'none' || paymentLinkOption.value === 'bank_account')) {
                paymentLinkOption.value = 'bank_account';
                customPaymentLink.value = '';
            } else if (!bankAccount.payment_link && paymentLinkOption.value === 'bank_account') {
                paymentLinkOption.value = 'none';
                customPaymentLink.value = '';
            }
        }
    }
};

const openBankAccountModal = () => {
    editingBankAccount.value = selectedBankAccount.value;
    showBankAccountModal.value = true;
};

const createNewBankAccount = () => {
    editingBankAccount.value = null;
    showBankAccountModal.value = true;
};

const handleBankAccountSaved = () => {
    showBankAccountModal.value = false;
    router.reload({ only: ['companies', 'bankAccounts'], preserveScroll: true, preserveState: true });
};



const addPredefinedTax = () => {
    if (selectedFieldType.value) {
        const setting = availableSettings.value.find(s => s.key === selectedFieldType.value);
        if (setting) {
            addInvoiceTax(setting.id);
        }
        selectedFieldType.value = null;
        showAddFieldDialog.value = false;
    }
};

const addInvoiceTax = (settingId) => {
    const setting = availableSettings.value.find(s => s.id === settingId);
    if (setting && !selectedInvoiceTaxes.value.find(t => t.invoice_setting_id === settingId)) {
        selectedInvoiceTaxes.value.push({
            invoice_setting_id: settingId,
            value: parseFloat(setting.value),
            setting: setting
        });
    }
};

const removeInvoiceTax = (index) => {
    selectedInvoiceTaxes.value.splice(index, 1);
};

const updateInvoiceTaxValue = (index, newValue) => {
    if (selectedInvoiceTaxes.value[index]) {
        selectedInvoiceTaxes.value[index].value = parseFloat(newValue);
    }
};

const openCustomTaxModal = () => {
    customTaxForm.reset();
    showCustomTaxModal.value = true;
};

const closeCustomTaxModal = () => {
    showCustomTaxModal.value = false;
    customTaxForm.reset();
};

const saveCustomTax = () => {
    customTaxForm.post(route('invoice-settings.store'), {
        onSuccess: () => {
            showCustomTaxModal.value = false;
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Custom tax created successfully'),
                life: 3000
            });
        },
        onError: (errors) => {
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to create custom tax'),
                life: 5000
            });
        }
    });
};

const calculateDynamicPaymentTax = () => {
    const subtotal = isEditMode.value ? calculateEditedSubtotal() : calculateSubtotal();
    let totalPaymentTax = 0;

    if (selectedInvoiceTaxes.value && selectedInvoiceTaxes.value.length > 0) {
        selectedInvoiceTaxes.value.forEach(tax => {
            if (tax.setting && tax.value > 0) {
                if (tax.setting.type === 'percentage') {
                    totalPaymentTax += (subtotal * tax.value / 100);
                } else if (tax.setting.type === 'fixed_amount') {
                    totalPaymentTax += (tax.value * 100);
                }
            }
        });
    }

    return totalPaymentTax;
};

const calculateTotalWithDynamicFields = () => {
    const subtotal = isEditMode.value ? calculateEditedSubtotal() : calculateSubtotal();

    const paymentTax = isEditMode.value ? calculateEditedPaymentTax() : calculatePaymentTax();

    const dynamicTaxes = calculateDynamicPaymentTax();

    let total = subtotal + paymentTax + dynamicTaxes;

    if (invoiceData.vatRate > 0) {
        total += (subtotal * invoiceData.vatRate / 100);
    }

    if (invoiceData.discount > 0) {
        total -= (invoiceData.discount * 100);
    }

    return Math.max(0, total);
};

const calculateEditedSubtotal = () => {
    if (!isEditMode.value) return calculateSubtotal();

    let total = 0;
    invoiceItems.value.forEach(item => {
        total += item.editablePrice * 100;
    });
    return total;
};

const calculateEditedPaymentTax = () => {
    if (!isEditMode.value) return calculatePaymentTax();
    return (invoiceData.paymentTax || 0) * 100;
};

const viewClientDetails = () => {
    const clientId = props.isCreateMode ? props.order?.client_id : props.invoice?.client_id;
    if (clientId) {
        router.visit(route('clients.edit', clientId));
    }
};

const handlePaymentLinkValueUpdate = (value) => {
};

const handlePaymentLinkOptionUpdate = (option) => {
    paymentLinkOption.value = String(option || 'none');
};

const handleCustomPaymentLinkUpdate = (value) => {
    customPaymentLink.value = String(value || '');
};
</script>

<template>
    <div :class="wrapperClass">
        <ConfirmDialog group="invoice-generate" />
        <Card>
            <template #header>
                <div :class="[
                    'flex items-center justify-between p-6 pb-0',
                    userType === 'client' ? 'flex-col sm:flex-row items-start sm:items-center gap-3' : ''
                ]">
                    <div class="flex items-center gap-3">
                        <i :class="[
                            'pi pi-file-pdf',
                            isPaymentDeferred ? 'text-blue-600' : 'text-green-600',
                            userType === 'client' ? 'text-xl sm:text-2xl' : 'text-2xl'
                        ]" />
                        <div>
                            <h2 :class="[
                                'font-bold',
                                userType === 'client' ? 'text-lg sm:text-xl' : 'text-xl'
                            ]">{{ $t('Invoice') }}</h2>
                            <span :class="[
                                'text-surface-500',
                                userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                            ]">{{ isCreateMode ? $t('New Invoice') : `#${invoice?.number || ''}` }}</span>
                        </div>
                    </div>

                    <div class="flex gap-2">
                        <Button
                            v-if="userType === 'admin' && !isCreateMode"
                            :label="isEditMode ? $t('Cancel') : $t('Edit')"
                            :icon="isEditMode ? 'pi pi-times' : 'pi pi-pencil'"
                            :severity="isEditMode ? 'secondary' : 'info'"
                            :size="buttonSize"
                            @click="toggleEditMode"
                        />
                        <Button
                            :label="$t('Back to Orders')"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            :size="buttonSize"
                            @click="goBack"
                        />
                    </div>
                </div>
            </template>

            <template #content>
                <div :class="userType === 'client' ? 'space-y-4 sm:space-y-6' : 'space-y-6'">
                    <!-- Payment Status Indicator -->
                    <div v-if="canShowPaymentActions" class="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div class="flex items-center gap-2 text-blue-700">
                            <i class="pi pi-clock text-xl"></i>
                            <div>
                                <p class="font-medium">{{ $t('Deferred Payment Pending') }}</p>
                                <p class="text-sm">{{ $t('Payment confirmation is done after complete this invoice.') }}</p>
                            </div>
                        </div>
                    </div>
                    <!-- Invoice Summary -->
                    <div :class="[
                        'flex flex-col sm:flex-row justify-between w-[70%]',
                        userType === 'client' ? 'gap-6 sm:gap-12' : 'gap-8 sm:gap-16'
                    ]">
                        <!-- Invoice Number -->
                        <div>
                            <label class="block text-sm font-medium text-surface-500 mb-1">
                                {{ $t('Invoice Number') }}
                            </label>
                            <div :class="[
                                'font-semibold text-surface-900',
                                userType === 'client' ? 'text-base sm:text-lg' : 'text-lg'
                            ]">
                                {{ isCreateMode ? $t('Auto-generated') : `#${invoice?.number || ''}` }}
                            </div>
                        </div>

                        <!-- Invoice Date -->
                        <div>
                            <label class="block text-sm font-medium text-surface-500 mb-1">
                                {{ $t('Invoice Date') }}
                            </label>
                            <div :class="[
                                'text-surface-900',
                                userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                            ]">
                                {{ isCreateMode ? formatDate(new Date()) : formatDate(invoice?.created_at) }}
                            </div>
                        </div>

                        <!-- Order Number -->
                        <div>
                            <label class="block text-sm font-medium text-surface-500 mb-1">
                                {{ $t('Order Number') }}
                            </label>
                            <div :class="[
                                'text-surface-900',
                                userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                            ]">
                                #{{ order.number }}
                            </div>
                        </div>
                    </div>

                    <Divider />

                    <!-- Invoice Items -->
                    <div>
                        <h3 :class="[
                            'font-semibold',
                            userType === 'client' ? 'text-base sm:text-lg mb-3 sm:mb-4' : 'text-lg mb-4'
                        ]">{{ $t('Invoice Items') }}</h3>

                        <!-- Mobile: Card View (Client only) -->
                        <div v-if="userType === 'client'" class="block lg:hidden space-y-3">
                            <Card v-for="(item, index) in invoiceItems" :key="index" class="p-3">
                                <template #content>
                                    <div class="space-y-2">
                                        <div>
                                            <span class="text-xs font-medium text-surface-500">{{ $t('Domain') }}</span>
                                            <div class="font-medium text-sm break-all">{{ item.domain }}</div>
                                        </div>
                                        <div>
                                            <span class="text-xs font-medium text-surface-500">{{ $t('Price') }}</span>
                                            <div class="font-semibold text-green-600 text-sm">{{ item.price }}</div>
                                        </div>

                                        <div>
                                            <span class="text-xs font-medium text-surface-500">{{ $t('Live Link') }}</span>
                                            <div>
                                                <a
                                                    v-if="item.live_link !== 'N/A'"
                                                    :href="item.live_link"
                                                    target="_blank"
                                                    class="text-blue-600 hover:underline text-xs break-all"
                                                >
                                                    {{ item.live_link }}
                                                    <i class="pi pi-external-link ml-1 text-xs"></i>
                                                </a>
                                                <span v-else class="text-surface-400 text-xs">{{ item.live_link }}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <span class="text-xs font-medium text-surface-500">{{ $t('Confirmed At') }}</span>
                                            <div>
                                                <Tag
                                                    v-if="item.confirmed_at !== 'Not confirmed'"
                                                    :value="item.confirmed_at"
                                                    severity="success"
                                                    icon="pi pi-check"
                                                    size="small"
                                                />
                                                <Tag
                                                    v-else
                                                    :value="item.confirmed_at"
                                                    severity="warn"
                                                    size="small"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </Card>
                        </div>

                        <!-- Desktop: Table View -->
                        <DataTable
                            :value="invoiceItems"
                            :paginator="false"
                            responsiveLayout="scroll"
                            :class="userType === 'client' ? 'hidden lg:block' : ''"
                        >
                            <Column field="domain" :header="$t('Domain')" style="min-width: 200px">
                                <template #body="slotProps">
                                    <span class="font-medium">{{ slotProps.data.domain }}</span>
                                </template>
                            </Column>
                            <Column field="price" :header="$t('Price')" style="min-width: 150px">
                                <template #body="slotProps">
                                    <span class="font-semibold text-green-600">
                                        {{ slotProps.data.price }}
                                    </span>
                                </template>
                            </Column>

                            <Column field="live_link" :header="$t('Live Link')" style="min-width: 250px">
                                <template #body="slotProps">
                                    <a
                                        v-if="slotProps.data.live_link !== 'N/A'"
                                        :href="slotProps.data.live_link"
                                        target="_blank"
                                        class="text-blue-600 hover:underline text-sm break-all"
                                    >
                                        {{ slotProps.data.live_link }}
                                        <i class="pi pi-external-link ml-1 text-xs"></i>
                                    </a>
                                    <span v-else class="text-surface-400">{{ slotProps.data.live_link }}</span>
                                </template>
                            </Column>
                            <Column v-if="userType === 'admin'" :header="$t('Seller')" style="min-width: 150px">
                                <template #body="slotProps">
                                    <div v-if="slotProps.data.publication_recipient" class="flex flex-col gap-0.5">
                                        <span class="font-medium text-surface-900">{{ slotProps.data.publication_recipient.seller_name || '-' }}</span>
                                        <span class="text-xs text-surface-500">{{ slotProps.data.publication_recipient.seller_email || '-' }}</span>
                                        <span v-if="slotProps.data.publication_recipient.seller_phone" class="text-xs text-surface-400">{{ slotProps.data.publication_recipient.seller_phone }}</span>
                                        <Tag v-if="slotProps.data.publication_recipient.email_target === 'seller'" :value="$t('Receive the email')" severity="warn" icon="pi pi-envelope" class="mt-1 text-xs w-fit" />
                                    </div>
                                    <span v-else class="text-surface-400">—</span>
                                </template>
                            </Column>
                            <Column v-if="userType === 'admin'" :header="$t('Owner')" style="min-width: 150px">
                                <template #body="slotProps">
                                    <div v-if="slotProps.data.publication_recipient" class="flex flex-col gap-0.5">
                                        <span class="font-medium text-surface-900">{{ slotProps.data.publication_recipient.owner_name || '-' }}</span>
                                        <span class="text-xs text-surface-500">{{ slotProps.data.publication_recipient.owner_email || '-' }}</span>
                                        <span v-if="slotProps.data.publication_recipient.owner_phone" class="text-xs text-surface-400">{{ slotProps.data.publication_recipient.owner_phone }}</span>
                                        <Tag v-if="slotProps.data.publication_recipient.email_target === 'owner'" :value="$t('Receive the email')" severity="info" icon="pi pi-envelope" class="mt-1 text-xs w-fit" />
                                    </div>
                                    <span v-else class="text-surface-400">—</span>
                                </template>
                            </Column>
                            <Column field="confirmed_at" :header="$t('Confirmed At')" style="min-width: 120px">
                                <template #body="slotProps">
                                    <Tag
                                        v-if="slotProps.data.confirmed_at !== 'Not confirmed'"
                                        :value="slotProps.data.confirmed_at"
                                        severity="success"
                                        icon="pi pi-check"
                                    />
                                    <Tag
                                        v-else
                                        :value="slotProps.data.confirmed_at"
                                        severity="warn"
                                    />
                                </template>
                            </Column>
                        </DataTable>

                        <!-- Seller & Owner blocks (only when email is missing) -->
                        <div v-if="userType === 'admin'" class="mt-4 space-y-4">
                            <template v-for="item in invoiceItems" :key="'recipient-' + item.id">
                                <RecipientContactBlock
                                    v-if="item.publication_recipient && !item.publication_recipient.type"
                                    :recipient="item.publication_recipient"
                                    @updated="() => router.reload({ preserveScroll: true })"
                                />
                            </template>
                        </div>
                    </div>

                    <Divider />

                    <!-- Client Information Section -->
                    <ClientInformationSection
                        v-if="(isEditMode || isCreateMode) && userType === 'admin'"
                        :client="order.client"
                        @view-client-details="viewClientDetails"
                    />

                    <!-- Company and Bank Account Selection (Admin Mode) - Side by side -->
                    <div v-if="(isEditMode || isCreateMode) && userType === 'admin'" class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        <!-- Company Selection -->
                        <CompanySelectionSection
                            v-model:selectedCompany="selectedCompany"
                            :companies="companies"
                            @create-new="createNewCompany"
                            @edit-company="openCompanyModal"
                        />

                        <!-- Bank Account Selection -->
                        <BankAccountSelectionSection
                            v-model:selectedBankAccount="selectedBankAccount"
                            :bankAccounts="bankAccounts"
                            @create-new="createNewBankAccount"
                            @edit-bank-account="openBankAccountModal"
                        />
                    </div>

                    <!-- Payment Tax, Discount and VAT (Editable in Admin Mode) -->
                    <PaymentTaxDiscountVatSection
                        v-if="(isEditMode || isCreateMode) && userType === 'admin'"
                        v-model:paymentTax="invoiceData.paymentTax"
                        :paymentTaxCoin="invoiceData.paymentTaxCoin"
                        v-model:vatRate="invoiceData.vatRate"
                        v-model:discount="invoiceData.discount"
                        v-model:discountCoin="invoiceData.discountCoin"
                    />

                    <!-- Invoice Settings Taxes Section (New Structure) -->
                    <InvoiceSettingsSection
                        v-if="(isEditMode || isCreateMode) && userType === 'admin'"
                        :selectedInvoiceTaxes="selectedInvoiceTaxes"
                        @add-custom-tax="openCustomTaxModal"
                        @add-predefined-tax="showAddFieldDialog = true"
                        @update-tax-value="updateInvoiceTaxValue"
                        @remove-tax="removeInvoiceTax"
                    />

                    <Divider />

                    <!-- Total -->
                    <div class="flex justify-end">
                        <div class="w-full md:w-1/2 space-y-3">
                            <div class="flex justify-between items-center">
                                <span :class="[
                                    'text-surface-600',
                                    userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                                ]">{{ $t('Subtotal') }}</span>
                                <span :class="[
                                    'font-semibold',
                                    userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                                ]">
                                    {{ formatCurrency(isEditMode ? calculateEditedSubtotal() : calculateSubtotal(), invoiceCurrency) }}
                                </span>
                            </div>
                            <div v-if="(isEditMode ? calculateEditedPaymentTax() : calculatePaymentTax()) > 0" class="flex justify-between items-center">
                                <span :class="[
                                    'text-surface-600',
                                    userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                                ]">{{ $t('Payment Tax') }}</span>
                                <span :class="[
                                    'font-semibold text-orange-600',
                                    userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                                ]">
                                    {{ formatCurrency(isEditMode ? calculateEditedPaymentTax() : calculatePaymentTax(), invoiceCurrency) }}
                                </span>
                            </div>
                            <!-- Individual taxes list -->
                            <div v-if="selectedInvoiceTaxes && selectedInvoiceTaxes.length > 0">
                                <div
                                    v-for="(tax, index) in selectedInvoiceTaxes"
                                    :key="index"
                                    class="flex justify-between items-center"
                                >
                                    <span :class="[
                                        'text-surface-600',
                                        userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                                    ]">{{ tax.setting?.key || 'Tax' }}</span>
                                    <span :class="[
                                        'font-semibold text-purple-600',
                                        userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                                    ]">
                                        <span v-if="tax.setting?.type === 'percentage'">
                                            {{ formatCurrency((calculateSubtotal() * tax.value / 100), invoiceCurrency) }}
                                        </span>
                                        <span v-else>
                                            {{ formatCurrency(tax.value * 100, tax.setting?.currency || invoiceCurrency) }}
                                        </span>
                                    </span>
                                </div>
                            </div>
                            <div v-if="invoiceData.vatRate > 0" class="flex justify-between items-center">
                                <span :class="[
                                    'text-surface-600',
                                    userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                                ]">{{ $t('VAT') }} ({{ invoiceData.vatRate }}%)</span>
                                <span :class="[
                                    'font-semibold',
                                    userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                                ]">
                                    {{ formatCurrency(((isEditMode ? calculateEditedSubtotal() : calculateSubtotal()) * invoiceData.vatRate / 100), invoiceCurrency) }}
                                </span>
                            </div>
                            <div v-if="invoiceData.discount > 0" class="flex justify-between items-center">
                                <span :class="[
                                    'text-surface-600',
                                    userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                                ]">{{ $t('Discount') }}</span>
                                <span :class="[
                                    'font-semibold text-red-600',
                                    userType === 'client' ? 'text-sm sm:text-base' : 'text-base'
                                ]">
                                    -{{ formatCurrency(invoiceData.discount * 100, invoiceData.discountCoin) }}
                                </span>
                            </div>

                            <div class="flex justify-between items-center">
                                <span :class="[
                                    'font-bold text-surface-900',
                                    userType === 'client' ? 'text-base sm:text-lg' : 'text-lg'
                                ]">{{ $t('Total') }}</span>
                                <span :class="[
                                    'font-bold text-green-600',
                                    userType === 'client' ? 'text-lg sm:text-xl' : 'text-xl'
                                ]">
                                    {{ formatCurrency(calculateTotalWithDynamicFields(), invoiceCurrency) }}
                                </span>
                            </div>
                        </div>
                    </div>


                    <!-- Payment Info -->
                    <PaymentInformationSection
                        :selected-bank-account="selectedBankAccount"
                        :invoice-data="invoiceData"
                        :is-edit-mode="isEditMode"
                        :user-type="userType"
                        :is-invoice-mode="true"
                        :payment-link-value="finalPaymentLink"
                        @update:paymentLinkValue="handlePaymentLinkValueUpdate"
                        @paymentLinkOption:update="handlePaymentLinkOptionUpdate"
                        @customPaymentLink:update="handleCustomPaymentLinkUpdate"
                    />

                    <!-- Invoice generation lock notice (Create Mode Only) -->
                    <div
                        v-if="isCreateMode && userType === 'admin'"
                        class="flex items-center gap-2 text-sm text-surface-500"
                    >
                        <i class="pi pi-exclamation-triangle text-amber-500" />
                        <span>{{ $t('Generating the invoice locks item editing for this order.') }}</span>
                    </div>

                    <!-- Edit Mode Actions -->
                    <div
                        v-if="isEditMode && userType === 'admin'"
                        class="border-t pt-4 flex items-center justify-end gap-6"
                    >
                        <!-- Send to Client Checkbox (Create Mode Only) -->
                        <div
                            v-if="isCreateMode"
                            class="flex items-center gap-2"
                        >
                            <Checkbox
                                v-model="sendToClient"
                                :binary="true"
                                inputId="sendToClient"
                            />
                            <label
                                for="sendToClient"
                                class="text-sm font-medium text-surface-700 leading-none"
                            >
                                {{ $t('Send to Client') }}
                            </label>
                        </div>

                        <!-- Action Buttons -->
                        <div class="flex items-center gap-3">
                            <Button
                                :label="$t('Preview PDF')"
                                icon="pi pi-eye"
                                severity="info"
                                @click="showPreview"
                            />
                            <Button
                                :label="isCreateMode ? $t('Generate Invoice') : $t('Save Changes')"
                                :icon="isCreateMode ? 'pi pi-plus' : 'pi pi-save'"
                                severity="success"
                                @click="saveInvoiceChanges"
                                :loading="isGeneratingInvoice"
                                :disabled="isGeneratingInvoice || (isCreateMode && !canGenerateInvoice)"
                            />
                        </div>
                    </div>


                    <!-- Actions -->
                    <div v-if="!isEditMode" :class="[
                        'flex gap-3 border-t',
                        userType === 'client' ? 'flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4' : 'flex-col sm:flex-row pt-4'
                    ]">
                        <Button
                            v-if="canDownloadPDF"
                            :label="$t('Download PDF')"
                            icon="pi pi-download"
                            severity="success"
                            :size="buttonSize"
                            :class="userType === 'client' ? 'w-full sm:w-auto' : ''"
                            @click="downloadInvoice"
                        />

                        <Button
                            v-if="isCreateMode"
                            :label="$t('Generate Invoice')"
                            icon="pi pi-plus"
                            severity="success"
                            :size="buttonSize"
                            @click="showPreview"
                            :disabled="!canGenerateInvoice"
                        />

                    </div>
                </div>
            </template>
        </Card>

        <!-- Add Predefined Tax Dialog -->
        <Dialog
            v-model:visible="showAddFieldDialog"
            :style="{ width: '500px' }"
            :header="$t('Add Predefined Tax')"
            :modal="true"
        >
            <div class="space-y-4">
                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Select Tax Type') }}</label>
                    <Dropdown
                        v-model="selectedFieldType"
                        :options="fieldTypes"
                        optionLabel="label"
                        optionValue="value"
                        :placeholder="$t('Choose a predefined tax type')"
                        class="w-full"
                    />
                </div>
                <div v-if="selectedFieldType" class="p-3 bg-surface-50 dark:bg-surface-700 rounded">
                    <p class="text-sm text-surface-600 dark:text-surface-300">
                        {{ $t('This will add') }}: <strong>{{ fieldTypes.find(f => f.value === selectedFieldType)?.label }}</strong>
                    </p>
                    <p class="text-xs text-surface-500 mt-1">
                        {{ $t('Default rate') }}: {{ fieldTypes.find(f => f.value === selectedFieldType)?.defaultRate }}{{ fieldTypes.find(f => f.value === selectedFieldType)?.type === 'percentage' ? '%' : ' BRL' }}
                    </p>
                </div>
            </div>
            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    text
                    @click="showAddFieldDialog = false"
                />
                <Button
                    :label="$t('Add Tax')"
                    icon="pi pi-plus"
                    severity="success"
                    @click="addPredefinedTax"
                    :disabled="!selectedFieldType"
                />
            </template>
        </Dialog>

        <!-- PDF Preview Modal -->
        <Dialog
            v-model:visible="showPreviewModal"
            :style="{ width: '90vw', height: '90vh' }"
            :header="$t('Invoice PDF Preview')"
            :modal="true"
            :maximizable="true"
        >
            <div class="h-full">
                <div class="bg-white p-5 border rounded shadow-sm overflow-auto h-full" style="font-family: 'DejaVu Sans', sans-serif; font-size: 11px; color: #333; line-height: 1.5;">
                    <div class="max-w-4xl mx-auto">
                        <!-- Header -->
                        <div class="text-center mb-8 pb-5 border-b-2 bg-gray-50" style="border-color: #0066cc;">
                            <div class="flex justify-between gap-6 items-start">
                                <div class="flex-1 text-left">
                                    <h1 class="font-bold mb-2" style="color: #0066cc; font-size: 26px;">INVOICE</h1>

                                    <p class="font-semibold mb-1" style="font-size: 18px; color: #444;">
                                        #{{ invoiceData.number }}
                                    </p>

                                    <p class="text-sm mb-1">
                                        <strong>Order #:</strong> {{ order.number }}
                                    </p>

                                    <p class="text-sm" style="color: #666;">
                                        <strong>Date:</strong> {{ formatDate(invoiceData.created_at) }}
                                    </p>
                                </div>

                                <div v-if="selectedCompany" class="flex-1">
                                    <div class="p-4 rounded text-left">
                                        <h2 class="font-semibold mb-2" style="font-size: 16px; color: #333;">
                                            {{ selectedCompany.name }}
                                        </h2>

                                        <p class="text-xs text-gray-600 mb-1">
                                            {{ selectedCompany.address_street + (selectedCompany.address_number ? " " + selectedCompany.address_number : "") }}
                                        </p>

                                        <p class="text-xs text-gray-600 mb-1">
                                            {{ selectedCompany.address_zipcode + ", " + selectedCompany.address_city }}
                                        </p>

                                        <p class="text-xs text-gray-600 mb-3">
                                            {{ selectedCompany.address_state + ", " + selectedCompany.address_country }}
                                        </p>

                                        <div class="">
                                            <p v-if="selectedCompany.document_number" class="text-xs text-gray-500">EIN: {{ selectedCompany.document_number }}</p>
                                            <p v-if="selectedCompany.vat_number" class="text-xs text-gray-500">VAT: {{ selectedCompany.vat_number }}</p>
                                            <p v-if="selectedCompany.email" class="text-xs text-gray-500">{{ selectedCompany.email }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex-1">

                                    <div class="p-4 rounded text-left">
                                        <div class="font-bold mb-1" style="font-size: 16px;">Client Data</div>
                                        <p class="font-semibold mb-1">
                                            {{ order.client?.invoice_company_name || order.client?.company_name || order.client?.name }}
                                        </p>

                                        <p v-if="order.client?.email" class="text-xs text-gray-600">{{ order.client.email }}</p>
                                        <p v-if="order.client?.invoice_address_country || order.client?.address_country || order.client?.country" class="text-xs text-gray-600">
                                            {{ order.client.invoice_address_country || order.client.address_country || order.client.country }}
                                        </p>
                                        <p v-if="order.client?.invoice_address_state || order.client?.address_state" class="text-xs text-gray-600">{{ order.client.invoice_address_state || order.client.address_state }}</p>

                                        <p v-if="order.client?.invoice_address_street_name || order.client?.address_street_name" class="text-xs text-gray-600">
                                            {{ order.client.invoice_address_street_name || order.client.address_street_name }}{{ (order.client?.invoice_address_street_number || order.client?.invoice_address_number || order.client?.address_number) ? ', ' + (order.client.invoice_address_street_number || order.client.invoice_address_number || order.client.address_number) : '' }}
                                        </p>

                                        <p v-if="order.client?.invoice_address_zipcode || order.client?.address_zipcode" class="text-xs text-gray-600">{{ order.client.invoice_address_zipcode || order.client.address_zipcode }}</p>

                                        <p v-if="order.client?.invoice_vat_number || order.client?.vat_id" class="text-xs text-gray-600 mt-1">
                                            <strong>VAT ID:</strong> {{ order.client.invoice_vat_number || order.client.vat_id }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                            <div class="flex-1">
                                <div class="font-bold mb-4" style="font-size: 14px; color: #0066cc;">SERVICES PROVIDED</div>
                                <div class="overflow-auto max-h-96">
                                    <table class="w-full border-collapse">
                                        <thead class="sticky top-0">
                                            <tr>
                                                <th class="p-2 text-left font-bold text-white" style="background-color: #0066cc; width: 8%;">#</th>
                                                <th class="p-2 text-left font-bold text-white" style="background-color: #0066cc; width: 67%;">Description</th>
                                                <th class="p-2 text-right font-bold text-white" style="background-color: #0066cc; width: 25%;">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(item, index) in invoiceItems" :key="index" :class="index % 2 === 1 ? 'bg-gray-50' : ''" style="background-color: #f9f9f9;">
                                                <td class="p-2 border-b border-gray-300">{{ index + 1 }}</td>
                                                <td class="p-2 border-b border-gray-300">
                                                    <div class="font-semibold mb-1">Link Building Service - {{ item.domain }}</div>
                                                    <div v-if="item.live_link !== 'N/A'" class="text-xs" style="color: #0066cc;">Live Link: {{ item.live_link }}</div>
                                                </td>
                                                <td class="p-2 border-b border-gray-300 text-right font-semibold">{{ item.currency_symbol }} {{ (item.originalSale / 100).toFixed(2) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div class="flex-1">
                                <div class="font-bold mb-4" style="font-size: 14px; color: #0066cc;">INVOICE SUMMARY</div>
                                <div class="bg-gray-50 p-5 rounded border h-fit" style="background-color: #f9f9f9; border-radius: 5px;">
                                    <div class="space-y-1">
                                        <div class="flex justify-between py-1">
                                            <span class="text-gray-600" style="font-size: 11px;">Subtotal:</span>
                                            <span class="font-semibold" style="font-size: 11px;">{{ getCurrencySymbol(invoiceCurrency) }} {{ (calculateSubtotal() / 100).toFixed(2) }}</span>
                                        </div>
                                        <div v-if="(isEditMode ? calculateEditedPaymentTax() : calculatePaymentTax()) > 0" class="flex justify-between py-1">
                                            <span class="text-gray-600" style="font-size: 11px;">Payment Processing:</span>
                                            <span class="font-semibold" style="font-size: 11px;">{{ getCurrencySymbol(invoiceCurrency) }} {{ ((isEditMode ? calculateEditedPaymentTax() : calculatePaymentTax()) / 100).toFixed(2) }}</span>
                                        </div>
                                        <div v-for="tax in selectedInvoiceTaxes" :key="tax.invoice_setting_id" class="flex justify-between py-1">
                                            <span class="text-gray-600" style="font-size: 11px;">{{ tax.setting.description }}{{ tax.setting.type === 'percentage' ? ' (' + tax.value + '%)' : '' }}:</span>
                                            <span class="font-semibold" style="font-size: 11px;">
                                                {{ getCurrencySymbol(invoiceCurrency) }}
                                                {{ tax.setting.type === 'percentage' ?
                                                    ((calculateSubtotal() * tax.value / 100) / 100).toFixed(2) :
                                                    (tax.value).toFixed(2)
                                                }}
                                            </span>
                                        </div>
                                        <div v-if="invoiceData.vatRate > 0" class="flex justify-between py-1">
                                            <span class="text-gray-600" style="font-size: 11px;">VAT ({{ invoiceData.vatRate }}%):</span>
                                            <span class="font-semibold" style="font-size: 11px;">{{ getCurrencySymbol(invoiceCurrency) }} {{ ((calculateSubtotal() * invoiceData.vatRate / 100) / 100).toFixed(2) }}</span>
                                        </div>
                                        <div v-if="invoiceData.discount > 0" class="flex justify-between py-1">
                                            <span style="font-size: 11px; color: #dc3545;">Discount:</span>
                                            <span class="font-semibold" style="font-size: 11px; color: #dc3545;">-{{ getCurrencySymbol(invoiceData.discountCoin) }} {{ invoiceData.discount.toFixed(2) }}</span>
                                        </div>
                                        <div class="border-t pt-3 mt-3" style="border-top: 1px solid #ddd;">
                                            <div class="flex justify-between py-1">
                                                <span class="font-bold" style="font-size: 14px; color: #333;">Total Due:</span>
                                                <span class="font-bold" style="font-size: 14px; color: #0066cc;">{{ getCurrencySymbol(invoiceCurrency) }} {{ (calculateTotalWithDynamicFields() / 100).toFixed(2) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="(paymentLinkOption === 'bank_account' && selectedBankAccount) || (paymentLinkOption === 'custom' && finalPaymentLink && finalPaymentLink.trim() !== '')" class="mt-10 p-5 border-l-4 border-gray-200" style="background-color: #f5f5f5; border-left-color: #0066cc;">
                            <h3 class="font-semibold mb-4 pb-2 border-b" style="color: #0066cc; border-bottom: 1px solid #ddd;">PAYMENT INFORMATION</h3>

                            <div class="mb-4">
                                <div v-if="paymentLinkOption === 'bank_account' && selectedBankAccount" class="bg-white p-2 mb-1 rounded border" style="border: 1px solid #ddd;">
                                    <p style="font-size: 11px;"><strong style="color: #666;">Payment Method:</strong> {{ selectedBankAccount.name }}</p>
                                </div>
                                <div v-if="(paymentLinkOption === 'bank_account' && selectedBankAccount?.payment_link) || (paymentLinkOption === 'custom' && finalPaymentLink && finalPaymentLink.trim() !== '')" class="bg-white p-2 mb-1 rounded border" style="border: 1px solid #ddd;">
                                    <p style="font-size: 11px; margin: 2px 0;"><strong style="color: #666;">Payment Link:</strong></p>
                                    <p class="text-xs break-all" style="font-size: 9px; color: #0066cc;">{{ finalPaymentLink }}</p>
                                </div>
                                <div class="bg-white p-2 mb-1 rounded border" style="border: 1px solid #ddd;">
                                    <p style="font-size: 11px;"><strong style="color: #666;">Payment Reference:</strong> Invoice #{{ invoiceData.number }}</p>
                                </div>
                            </div>

                            <div v-if="paymentLinkOption === 'bank_account' && selectedBankAccount" class="grid grid-cols-2 gap-4">
                                <div>
                                    <p><strong>Bank Details:</strong></p>
                                    <p v-if="selectedBankAccount.bic_swift"><strong>BIC:</strong> {{ selectedBankAccount.bic_swift }}</p>
                                    <p><strong>Bank Name:</strong> {{ selectedBankAccount.bank_name }}</p>
                                    <p v-if="selectedBankAccount.bank_address"><strong>Address:</strong> {{ selectedBankAccount.bank_address }}</p>
                                    <p v-if="selectedBankAccount.bank_city || selectedBankAccount.bank_zipcode">
                                        {{ selectedBankAccount.bank_city }}{{ selectedBankAccount.bank_zipcode ? ', ' + selectedBankAccount.bank_zipcode : '' }}
                                    </p>
                                    <p v-if="selectedBankAccount.bank_country">{{ selectedBankAccount.bank_country }}</p>
                                    <p v-if="selectedBankAccount.account_number"><strong>Account Number:</strong> {{ selectedBankAccount.account_number }}</p>
                                    <p v-if="selectedBankAccount.agency_number"><strong>Agency:</strong> {{ selectedBankAccount.agency_number }}</p>
                                </div>

                                <div>
                                    <p><strong>Recipient:</strong></p>
                                    <p>{{ selectedBankAccount.account_holder || (selectedCompany ? selectedCompany.name : '') }}</p>
                                    <p v-if="selectedCompany">
                                        {{ selectedCompany.address_street }}{{ selectedCompany.address_number ? ' ' + selectedCompany.address_number : '' }}
                                    </p>
                                    <p v-if="selectedCompany">{{ selectedCompany.address_city }}</p>
                                    <p v-if="selectedCompany">{{ selectedCompany.address_state }}, {{ selectedCompany.address_country }}</p>
                                    <p v-if="selectedBankAccount.iban"><strong>IBAN:</strong> {{ selectedBankAccount.iban }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Footer -->
                        <div class="mt-10 pt-5 border-t text-center text-xs text-gray-500">
                            <p>Thank you for your business!</p>
                            <p>For any questions regarding this invoice, please contact {{ selectedCompany?.email || invoiceData.company_email }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <template #footer>
                <Button
                    :label="$t('Close Preview')"
                    icon="pi pi-times"
                    text
                    @click="showPreviewModal = false"
                />
                <Button
                    :label="isCreateMode ? $t('Generate Invoice') : $t('Save Changes')"
                    :icon="isCreateMode ? 'pi pi-plus' : 'pi pi-save'"
                    severity="success"
                    @click="saveInvoiceChanges"
                    :loading="isGeneratingInvoice"
                    :disabled="isGeneratingInvoice"
                />
            </template>
        </Dialog>

        <!-- Payment Request Mismatch Modal -->
        <PaymentRequestMismatchModal
            v-model:visible="showDivergenceDialog"
            :payment-request="paymentRequest"
            :order="order"
            :selected-company="selectedCompany"
            :selected-bank-account="selectedBankAccount"
            :final-payment-link="finalPaymentLink"
            @proceed="proceedWithDivergence"
            @cancel="cancelDivergence"
        />

        <!-- Edit Existing Invoice Confirmation Dialog -->
        <Dialog
            v-model:visible="showEditConfirmDialog"
            :style="{ width: '500px' }"
            :header="$t('Warning')"
            :modal="true"
        >
            <div class="flex items-start gap-4">
                <i class="pi pi-exclamation-triangle text-3xl text-amber-500" />
                <div>
                    <p class="font-semibold text-lg mb-2">{{ $t('Edit Generated Invoice') }}</p>
                    <p class="text-surface-600 mb-3">
                        {{ $t('This invoice has already been generated and sent to the client.') }}
                    </p>
                    <p class="text-surface-600">
                        {{ $t('Are you sure you want to modify it?') }}
                    </p>
                </div>
            </div>
            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    text
                    @click="cancelEditConfirmation"
                />
                <Button
                    :label="$t('Yes, Edit Invoice')"
                    icon="pi pi-pencil"
                    severity="warning"
                    @click="confirmEditExistingInvoice"
                />
            </template>
        </Dialog>

        <!-- Send New Version Dialog -->
        <Dialog
            v-model:visible="showSendEmailDialog"
            :style="{ width: '500px' }"
            :header="$t('Send Updated Invoice')"
            :modal="true"
        >
            <div class="flex items-start gap-4">
                <i class="pi pi-send text-3xl text-blue-500" />
                <div>
                    <p class="font-semibold text-lg mb-2">{{ $t('Send New Version to Client?') }}</p>
                    <p class="text-surface-600 mb-3">
                        {{ $t('Do you want to send the updated invoice to the client?') }}
                    </p>
                </div>
            </div>
            <ClientEmailMessagePanel
                class="mt-3"
                email-type="invoice"
                :client-locale="order?.client?.locale || 'en'"
                @change="payload => invoiceEmailOptions = payload"
            />
            <template #footer>
                <Button
                    :label="$t('No, Skip')"
                    icon="pi pi-times"
                    text
                    @click="skipSendingEmail"
                    :disabled="sendEmailForm.processing"
                />
                <Button
                    :label="$t('Yes, Send Email')"
                    icon="pi pi-send"
                    severity="primary"
                    @click="sendNewInvoiceVersion"
                    :loading="sendEmailForm.processing"
                    :disabled="sendEmailForm.processing"
                />
            </template>
        </Dialog>

        <!-- Value Difference Alert Dialog -->
        <Dialog
            v-model:visible="showValueDifferenceDialog"
            :style="{ width: '500px' }"
            :header="$t('Warning')"
            :modal="true"
        >
            <div class="flex items-start gap-4">
                <i class="pi pi-exclamation-triangle text-3xl text-amber-500" />
                <div class="flex-1">
                    <p class="font-semibold text-lg mb-2">{{ $t('Values Modified') }}</p>
                    <p class="text-surface-600 mb-4">
                        {{ $t('You have modified the invoice values from the original order.') }}
                    </p>
                    <p class="text-surface-600 text-sm">
                        {{ $t('Do you want to continue with these changes?') }}
                    </p>
                </div>
            </div>
            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    text
                    @click="cancelSave"
                />
                <Button
                    :label="$t('Yes, Continue')"
                    icon="pi pi-check"
                    severity="warning"
                    @click="proceedWithSave"
                />
            </template>
        </Dialog>

        <!-- Company Modal -->
        <CompanyFormModal
            v-model="showCompanyModal"
            :company="editingCompany"
            @saved="handleCompanySaved"
        />

        <!-- Bank Account Modal -->
        <BankAccountFormModal
            v-model="showBankAccountModal"
            :bank-account="editingBankAccount"
            :companies="companies"
            @saved="handleBankAccountSaved"
        />

        <!-- Invoice Settings Modal -->
        <Dialog
            v-model:visible="showInvoiceSettingsModal"
            :style="{ width: '800px' }"
            :header="$t('Select Invoice Settings')"
            :modal="true"
        >
            <div class="space-y-4">
                <div v-for="(settings, group) in invoiceSettings" :key="group" class="border rounded-lg p-4">
                    <h4 class="font-semibold text-lg mb-3 capitalize">{{ group }}</h4>
                    <div class="grid grid-cols-1 gap-3">
                        <div
                            v-for="setting in settings"
                            :key="setting.id"
                            class="flex items-center justify-between p-3 border rounded hover:bg-surface-50 dark:hover:bg-surface-700"
                        >
                            <div class="flex-1">
                                <div class="font-medium">{{ setting.key }}</div>
                                <div class="text-sm text-surface-600">{{ setting.description }}</div>
                                <div class="text-xs text-surface-500 mt-1">
                                    Default: {{ setting.value }}{{ setting.type === 'percentage' ? '%' : ' ' + setting.currency }}
                                </div>
                            </div>
                            <Button
                                v-if="!selectedInvoiceTaxes.find(t => t.invoice_setting_id === setting.id)"
                                :label="$t('Add')"
                                icon="pi pi-plus"
                                size="small"
                                @click="addInvoiceTax(setting.id)"
                            />
                            <Badge v-else value="Added" severity="success" />
                        </div>
                    </div>
                </div>
            </div>
            <template #footer>
                <Button
                    :label="$t('Close')"
                    icon="pi pi-times"
                    text
                    @click="showInvoiceSettingsModal = false"
                />
            </template>
        </Dialog>

        <!-- Custom Tax Modal -->
        <Dialog
            v-model:visible="showCustomTaxModal"
            :style="{ width: '600px' }"
            :header="$t('Create Custom Tax')"
            :modal="true"
        >
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="col-span-2">
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Tax Key/Name') }} *</label>
                    <InputText
                        v-model="customTaxForm.key"
                        class="w-full"
                        :class="{ 'p-invalid': customTaxForm.errors.key }"
                        placeholder="e.g., custom_fee_1"
                    />
                    <small class="p-error" v-if="customTaxForm.errors.key">{{ customTaxForm.errors.key }}</small>
                </div>

                <div class="col-span-2">
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Description') }} *</label>
                    <InputText
                        v-model="customTaxForm.description"
                        class="w-full"
                        :class="{ 'p-invalid': customTaxForm.errors.description }"
                        placeholder="e.g., Custom processing fee"
                    />
                    <small class="p-error" v-if="customTaxForm.errors.description">{{ customTaxForm.errors.description }}</small>
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Default Value') }} *</label>
                    <InputNumber
                        v-model="customTaxForm.value"
                        :step="0.01"
                        :minFractionDigits="2"
                        :maxFractionDigits="2"
                        class="w-full"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Type') }} *</label>
                    <Dropdown
                        v-model="customTaxForm.type"
                        :options="[
                            { label: $t('Percentage'), value: 'percentage' },
                            { label: $t('Fixed Amount'), value: 'fixed_amount' }
                        ]"
                        optionLabel="label"
                        optionValue="value"
                        class="w-full"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Currency') }}</label>
                    <Dropdown
                        v-model="customTaxForm.currency"
                        :options="['USD', 'EUR', 'BRL', 'GBP', '%']"
                        class="w-full"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Group') }}</label>
                    <Dropdown
                        v-model="customTaxForm.group"
                        :options="[
                            { label: $t('Fees'), value: 'fees' },
                            { label: $t('Costs'), value: 'costs' },
                            { label: $t('Exchange'), value: 'exchange' },
                            { label: $t('General'), value: 'general' }
                        ]"
                        optionLabel="label"
                        optionValue="value"
                        class="w-full"
                    />
                </div>
            </div>
            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    text
                    @click="closeCustomTaxModal"
                    :disabled="customTaxForm.processing"
                />
                <Button
                    :label="$t('Create Tax')"
                    icon="pi pi-save"
                    severity="success"
                    @click="saveCustomTax"
                    :loading="customTaxForm.processing"
                    :disabled="customTaxForm.processing"
                />
            </template>
        </Dialog>
    </div>
</template>

